import { useState, useEffect } from 'react';
import logger from '../lib/logger';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { getPaymentRequest, fulfillPaymentRequest, getWalletBalances } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  DollarSign, Loader2, CheckCircle2, XCircle, AlertCircle,
  Clock, User, ArrowRight, CreditCard
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const Pay = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { theme } = useTheme();
  
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [amount, setAmount] = useState('');
  const [balance, setBalance] = useState(0);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchRequest();
  }, [requestId]);

  useEffect(() => {
    if (isAuthenticated && request) {
      fetchBalance();
    }
  }, [isAuthenticated, request]);

  const fetchRequest = async () => {
    try {
      const { data } = await getPaymentRequest(requestId);
      setRequest(data);
      if (data.amount) {
        setAmount(data.amount.toString());
      }
    } catch (error) {
      logger.error('Error fetching request:', error);
      setRequest({ error: true });
    } finally {
      setLoading(false);
    }
  };

  const fetchBalance = async () => {
    try {
      const { data } = await getWalletBalances();
      const currencyBalance = data.find(b => b.currency === request?.currency);
      setBalance(currencyBalance?.balance || 0);
    } catch (error) {
      logger.error('Error fetching balance:', error);
    }
  };

  const handlePay = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    const fee = parseFloat(amount) * 0.015;
    const total = parseFloat(amount) + fee;

    if (total > balance) {
      toast.error('Insufficient funds');
      return;
    }

    setPaying(true);
    try {
      await fulfillPaymentRequest(requestId, parseFloat(amount));
      setSuccess(true);
      toast.success('Payment successful!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
    } finally {
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-background' : 'bg-slate-50'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (request?.error || !request) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${theme === 'dark' ? 'bg-background' : 'bg-slate-50'}`}>
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <XCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-heading font-semibold mb-2">Request Not Found</h2>
            <p className="text-muted-foreground mb-6">This payment request doesn't exist or has been removed.</p>
            <Link to="/">
              <Button className="rounded-xl">Go to ParachPay</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (request.status === 'expired') {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${theme === 'dark' ? 'bg-background' : 'bg-slate-50'}`}>
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <Clock className="w-16 h-16 mx-auto text-slate-400 mb-4" />
            <h2 className="text-xl font-heading font-semibold mb-2">Request Expired</h2>
            <p className="text-muted-foreground mb-6">This payment request has expired.</p>
            <Link to="/">
              <Button className="rounded-xl">Go to ParachPay</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (request.status === 'cancelled') {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${theme === 'dark' ? 'bg-background' : 'bg-slate-50'}`}>
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <XCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-heading font-semibold mb-2">Request Cancelled</h2>
            <p className="text-muted-foreground mb-6">This payment request has been cancelled.</p>
            <Link to="/">
              <Button className="rounded-xl">Go to ParachPay</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (request.status === 'completed') {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${theme === 'dark' ? 'bg-background' : 'bg-slate-50'}`}>
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500 mb-4" />
            <h2 className="text-xl font-heading font-semibold mb-2">Already Paid</h2>
            <p className="text-muted-foreground mb-6">This payment request has already been fulfilled.</p>
            <Link to="/">
              <Button className="rounded-xl">Go to ParachPay</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${theme === 'dark' ? 'bg-background' : 'bg-slate-50'}`}>
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="w-full max-w-md"
        >
          <Card>
            <CardContent className="py-12 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring" }}
              >
                <CheckCircle2 className="w-20 h-20 mx-auto text-emerald-500 mb-4" />
              </motion.div>
              <h2 className="text-2xl font-heading font-bold mb-2">Payment Sent!</h2>
              <p className="text-muted-foreground mb-2">
                You sent <span className="font-semibold">${parseFloat(amount).toFixed(2)} {request.currency}</span>
              </p>
              <p className="text-muted-foreground mb-6">to {request.user_name}</p>
              <Link to="/dashboard">
                <Button className="rounded-xl">Go to Dashboard</Button>
              </Link>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  const fee = amount ? parseFloat(amount) * 0.015 : 0;
  const total = amount ? parseFloat(amount) + fee : 0;

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${theme === 'dark' ? 'bg-background' : 'bg-slate-50'}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Card>
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <User className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="font-heading">{request.user_name}</CardTitle>
            <CardDescription>is requesting payment</CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Amount Display */}
            <div className="text-center py-4">
              {request.amount ? (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Amount Requested</p>
                  <p className="text-4xl font-heading font-bold text-emerald-500">
                    ${request.amount.toFixed(2)}
                  </p>
                  <p className="text-muted-foreground">{request.currency}</p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Enter Amount</p>
                  <p className="text-lg text-muted-foreground">You choose how much to send</p>
                </div>
              )}
            </div>

            {request.note && (
              <div className="p-3 rounded-xl bg-muted text-center">
                <p className="text-sm italic">"{request.note}"</p>
              </div>
            )}

            {!isAuthenticated ? (
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-amber-500/10 text-amber-600 text-center">
                  <AlertCircle className="w-5 h-5 mx-auto mb-2" />
                  <p className="text-sm">You need to log in to send payment</p>
                </div>
                <Link to={`/login?redirect=/pay/${requestId}`}>
                  <Button className="w-full rounded-xl">
                    Log In to Pay
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <p className="text-center text-sm text-muted-foreground">
                  Don't have an account?{' '}
                  <Link to={`/register?redirect=/pay/${requestId}`} className="text-emerald-500 hover:underline">
                    Sign up
                  </Link>
                </p>
              </div>
            ) : request.user_email === user?.email ? (
              <div className="p-4 rounded-xl bg-muted text-center">
                <p className="text-muted-foreground">This is your own payment request</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Amount Input (if flexible) */}
                {!request.amount && (
                  <div className="space-y-2">
                    <Label>Amount to Send</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="number"
                        placeholder="0.00"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="pl-9 h-12 text-lg rounded-xl"
                        min="0.01"
                        step="0.01"
                        data-testid="pay-amount-input"
                      />
                    </div>
                  </div>
                )}

                {/* Balance & Fee Summary */}
                {amount && parseFloat(amount) > 0 && (
                  <div className="space-y-2 p-4 rounded-xl bg-muted">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Your Balance</span>
                      <span className="font-mono">${balance.toFixed(2)} {request.currency}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Amount</span>
                      <span className="font-mono">${parseFloat(amount).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Fee (1.5%)</span>
                      <span className="font-mono text-orange-500">+${fee.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-semibold pt-2 border-t">
                      <span>Total</span>
                      <span className="font-mono">${total.toFixed(2)} {request.currency}</span>
                    </div>
                  </div>
                )}

                {/* Insufficient funds warning */}
                {total > balance && amount && (
                  <div className="p-3 rounded-xl bg-destructive/10 text-destructive text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Insufficient funds. You need ${(total - balance).toFixed(2)} more.
                  </div>
                )}

                <Button
                  onClick={handlePay}
                  disabled={paying || !amount || parseFloat(amount) <= 0 || total > balance}
                  className="w-full h-12 rounded-xl text-lg"
                  data-testid="pay-submit-btn"
                >
                  {paying ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pay ${amount ? parseFloat(amount).toFixed(2) : '0.00'}
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ParachPay Branding */}
        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            Powered by <span className="font-semibold text-emerald-500">ParachPay</span>
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default Pay;
 Pay;
